import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { css } from "@emotion/react";
import { axiosPath } from "@src/api/axiosPath";
import { useNavigate } from "react-router-dom";
import { updateUserSession } from "@src/api/axios";

const nestURL = process.env.REACT_APP_NEST_API_URL;

interface UserInfoProps {
    id: string;
    productName: string;
    uname: string;
}

interface RouteItem {
    label: string;
    path: string;
}

interface DoubleRouteItem {
    label: string;
    buttons: RouteItem[];
}

type RouteEntry = RouteItem | DoubleRouteItem;

interface RouteGroup {
    title: string;
    routes: RouteEntry[];
}

function isDoubleRoute(route: RouteEntry): route is DoubleRouteItem {
    return "buttons" in route;
}

const ROUTE_GROUPS: RouteGroup[] = [
    {
        title: "지도 / 검색",
        routes: [
            { label: "지도", path: "/map?search=0" },
            { label: "매물검색", path: "/searchItems" },
            { label: "맞춤형 신건 알림", path: "/favoriteRegion" },
            { label: "오늘의 신건 매물", path: "/items/daily-new" },
            { label: "지도 필터 (네이티브)", path: "/mapFilter" },
            { label: "전체 필터 (지도)", path: "/allFilter/map" },
            { label: "전체 필터 (검색)", path: "/allFilter/search" },
            { label: "실거래가 필터", path: "/realPriceFilter" },
            { label: "거리뷰", path: "/panoramaView" },
            { label: "현장 상세", path: "/fieldDetail" },
        ],
    },
    {
        title: "AI 필터",
        routes: [
            { label: "AI 맞춤필터", path: "/aiFilterV2" },
            { label: "소액 맞춤필터", path: "/smallFilter" },
        ],
    },
    {
        title: "등기부등본",
        routes: [
            { label: "등기부등본", path: "/deungi" },
            { label: "열람권 충전", path: "/deungi/payment" },
            { label: "등기 PDF 뷰어", path: "/deungi/pdf" },
            { label: "등기 열람 목록", path: "/deungi/list" },
            { label: "충전/사용 내역", path: "/deungi/charginguselist" },
            { label: "열람권 환불", path: "/deungi/refund" },
            { label: "열람권 취소", path: "/deungi/readingticketcancel" },
        ],
    },
    {
        title: "등기 변동 알림",
        routes: [
            { label: "등기 변동 알림", path: "/deungi/alarm" },
            { label: "알림 설정", path: "/deungi/alarm/setting" },
            { label: "알림 검색", path: "/deungi/alarm/search" },
            { label: "알림 상세", path: "/deungi/alarm/detail" },
            { label: "알림 신청 목록", path: "/deungi/alarm/list" },
        ],
    },
    {
        title: "법인 등기부등본",
        routes: [
            { label: "법인 등기부등본", path: "/corpDeungi" },
            { label: "법인 등기 충전", path: "/corpDeungi/payment" },
            { label: "법인 등기 PDF", path: "/corpDeungi/pdf" },
            { label: "법인 열람권 취소", path: "/corpDeungi/readingticketcancel" },
        ],
    },
    {
        title: "건축물대장",
        routes: [
            { label: "건축물대장", path: "/buildingRegister" },
            { label: "발급 목록", path: "/buildingRegister/list" },
            { label: "PDF 뷰어", path: "/buildingRegister/viewer" },
            { label: "충전", path: "/buildingRegister/payment" },
        ],
    },
    {
        title: "토지·임야대장",
        routes: [
            { label: "토지·임야대장", path: "/landRegister" },
            { label: "발급 목록", path: "/landRegister/list" },
            { label: "PDF 뷰어", path: "/landRegister/viewer" },
            { label: "발급 현황", path: "/landRegister/status" },
            { label: "발급 대기", path: "/landRegister/issue" },
            { label: "충전", path: "/landRegister/payment" },
        ],
    },
    {
        title: "캠퍼스",
        routes: [
            { label: "미스고 캠퍼스", path: "/campus" },
            { label: "캠퍼스 취소/환불", path: "/campus/refund" },
        ],
    },
    {
        title: "전문가",
        routes: [
            { label: "전문가 문의 관리", path: "/expert/inquiry" },
            { label: "전문가 정보", path: "/expert/info" },
            { label: "전문가 신청", path: "/expert/application" },
            { label: "전문가 사무실 검색", path: "/expert/officeSearch" },
            { label: "전문가 서비스 안내", path: "/expert/serviceNotice" },
            { label: "지역광고 안내 팝업", path: "/expert/localAd/introduction/popup" },
            { label: "광고 매물 관리", path: "/expertAdList" },
            { label: "전문가 검색", path: "/expertSearch" },
            { label: "대행 결제", path: "/expert/payment/agent" },
            { label: "컨설팅 결제", path: "/expert/payment/consulting" },
            { label: "동행 결제", path: "/expert/payment/companion" },
            { label: "매치프라이스 결제", path: "/expert/payment/matchPrice" },
        ],
    },
    {
        title: "광고",
        routes: [
            { label: "광고 결제", path: "/advertisement/payment" },
            { label: "광고 지역 선택", path: "/advertisement/selecter" },
            { label: "광고 목록", path: "/advertisement/list" },
            { label: "광고 필터", path: "/advertisement/filter" },
            { label: "광고 정보 (지역)", path: "/advertisement/info/region" },
            { label: "광고 정보 (매물)", path: "/advertisement/info/item" },
            { label: "광고권 취소/환불", path: "/advertisementCancelRefund" },
        ],
    },
    {
        title: "건축사 광고",
        routes: [
            { label: "건축사 정보", path: "/advertisement-architect/info" },
            { label: "건축사 지역 선택", path: "/advertisement-architect/selecter" },
            { label: "건축사 결제", path: "/advertisement-architect/payment" },
            { label: "건축사 목록", path: "/advertisement-architect/list" },
        ],
    },
    {
        title: "고스톱",
        routes: [
            { label: "주거/토지 고스톱", path: "/goStop" },
            { label: "고스톱 목록", path: "/goStopList" },
            { label: "고스톱 취소/환불", path: "/goStopRefund" },
            { label: "상가 고스톱", path: "/storeGoStop" },
            { label: "상가 고스톱 목록", path: "/storeGoStopList" },
            { label: "상가 고스톱 정산", path: "/storeGoStop/calculate" },
            { label: "상가 고스톱 취소/환불", path: "/storeGoStopRefund" },
            { label: "상가 고스톱 소개", path: "/storeGoStop/introduction" },
        ],
    },
    {
        title: "구독 / 환불",
        routes: [
            {
                label: "구독 상품",
                buttons: [
                    { label: "AOS 상품", path: "/subscriptionInfo?type=6" },
                    { label: "IOS 상품", path: "/ios-subscriptionInfo?type=7" },
                ],
            },
            { label: "환불하기", path: "/unSubscribe" },
            { label: "환불 상세", path: "/unSubscribe/refund" },
            { label: "탈퇴", path: "/unSubscribe/withDraw" },
            { label: "지역광고권 취소/환불", path: "/regionAdsRefund" },
            { label: "도로확장 계획 취소/환불", path: "/roadPlanRefund" },
        ],
    },
    {
        title: "국유지",
        routes: [
            {
                label: "국유지 이용권",
                buttons: [
                    { label: "AOS 상품", path: "/stateLandInfo?type=8" },
                    { label: "IOS 상품", path: "/stateLandInfo?type=7" },
                ],
            },
            { label: "국유지 이용권 취소/환불", path: "/stateLandRefund" },
            { label: "국공유지 필터 (네이티브)", path: "/stateLandUserFilter" },
        ],
    },
    {
        title: "미스고AI / 소유자 확인",
        routes: [
            { label: "미스고AI & 챗봇", path: "/missgoAI" },
            { label: "미스고AI PDF 뷰어", path: "/missgoAI/viewer" },
            { label: "소유자 열람권 충전", path: "/ownerCheck/payment" },
        ],
    },
    {
        title: "이벤트",
        routes: [
            { label: "AI 배당 이벤트", path: "/event/aiDividendEvent" },
            { label: "데이터맵 신규기능 이벤트", path: "/event/datamapNewFnEvent" },
            { label: "소유자확인 이벤트", path: "/event/ownerCheckServiceEvent" },
            { label: "미스고AI 배너 이벤트", path: "/event/missgoAiBannerEvent" },
            { label: "AI 챗봇 배너 이벤트", path: "/event/AiChatBotBannerEvent" },
            { label: "얼리버드 VIP50 이벤트", path: "/event/earlyBirdVip50" },
            { label: "광고 이벤트", path: "/event/AdvertisementEvent" },
            { label: "5건 무료 이벤트", path: "/event/FiveFreeEvent" },
            { label: "건축사 이벤트", path: "/event/ArchitectEvent" },
            { label: "임장동행 매칭 서비스", path: "/event/accompanying" },
        ],
    },
    {
        title: "기타",
        routes: [
            { label: "오늘 변경된 매물", path: "/StatusItem" },
            { label: "건물현황표", path: "/BuildingStatusTable" },
            { label: "소액임차보증금표", path: "/smallRentTable" },
            { label: "스피치 숏츠", path: "/speechShorts" },
            { label: "고객센터 문의", path: "/customerService/inquiry" },
            { label: "iOS 데이터맵 소개", path: "/introduction/IosDatamap" },
            { label: "쿠폰 등록 완료", path: "/successCoupon" },
            { label: "경매대행 채팅", path: "/auctionAgent/chatting" },
            { label: "샘플 입찰 목록", path: "/sampleBidList" },
            { label: "스토어 리다이렉트", path: "/redirectStore" },
            { label: "3D 뷰어", path: "/three" },
        ],
    },
];

function Login() {
    const navigate = useNavigate();
    const [loginInput, setLoginInput] = useState("");
    const [userInfo, setUserInfo] = useState<UserInfoProps | null>(null);
    const [isLogined, setIsLogined] = useState(false);
    const [openGroups, setOpenGroups] = useState<Set<number>>(new Set());
    const [searchKeyword, setSearchKeyword] = useState("");

    const fetchUserInfo = async () => {
        try {
            const response = await axios.get(`${nestURL}/user`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
            });
            setIsLogined(true);
            setUserInfo(response.data);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                if (error.response.status) {
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("refreshToken");
                }
            }
        }
    };

    useEffect(() => {
        fetchUserInfo();
    }, []);

    const handleMatchSuccessWithEmail = async () => {
        let email = "";
        let password = "";

        if (loginInput.startsWith("@")) {
            const withoutAt = loginInput.slice(1);
            [email, password] = withoutAt.split("/");
        } else if (loginInput.startsWith("t")) {
            const [rawEmail, pw] = loginInput.split("/");
            email = `${rawEmail}@msga.msg`;
            password = pw;
        } else {
            return;
        }

        await axios
            .post(`${axiosPath.login}`, {
                id: email,
                password: { password },
            })
            .then((res: any) => {
                const { accessToken, refreshToken } = res.data;
                localStorage.setItem("isSubscription", "true");
                localStorage.setItem("accessToken", accessToken);
                localStorage.setItem("refreshToken", refreshToken);
                updateUserSession(accessToken, refreshToken);
                fetchUserInfo();
            })
            .catch((e) => alert(e));
    };

    const handleLogout = () => {
        localStorage.removeItem("isSubscription");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        window.location.reload();
    };

    const toggleGroup = (index: number) => {
        setOpenGroups((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const toggleAll = () => {
        if (openGroups.size === ROUTE_GROUPS.length) {
            setOpenGroups(new Set());
        } else {
            setOpenGroups(new Set(ROUTE_GROUPS.map((_, i) => i)));
        }
    };

    const filteredGroups = useMemo(() => {
        if (!searchKeyword.trim()) return ROUTE_GROUPS;

        const keyword = searchKeyword.toLowerCase();
        return ROUTE_GROUPS.map((group) => ({
            ...group,
            routes: group.routes.filter((route) => {
                if (isDoubleRoute(route)) {
                    return (
                        route.label.toLowerCase().includes(keyword) ||
                        route.buttons.some(
                            (btn) => btn.label.toLowerCase().includes(keyword) || btn.path.toLowerCase().includes(keyword),
                        )
                    );
                }
                return route.label.toLowerCase().includes(keyword) || route.path.toLowerCase().includes(keyword);
            }),
        })).filter((group) => group.routes.length > 0);
    }, [searchKeyword]);

    const isSearching = searchKeyword.trim().length > 0;

    const renderRouteButton = (route: RouteEntry, index: number) => {
        if (isDoubleRoute(route)) {
            return (
                <div key={index} css={routeCardStyle}>
                    <span css={routeLabelStyle}>{route.label}</span>
                    <div css={multiButtonStyle}>
                        {route.buttons.map((btn, btnIndex) => (
                            <button key={btnIndex} css={navBtnStyle} onClick={() => navigate(btn.path)}>
                                {btn.label}
                            </button>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <button key={index} css={navBtnStyle} onClick={() => navigate(route.path)}>
                {route.label}
            </button>
        );
    };

    return (
        <div css={pageStyle}>
            {/* Login Section */}
            <div css={loginCardStyle}>
                {isLogined ? (
                    <>
                        <div css={userInfoStyle}>
                            <div css={userInfoRow}>
                                <span css={userInfoLabel}>ID</span>
                                <span>{userInfo?.id}</span>
                            </div>
                            <div css={userInfoRow}>
                                <span css={userInfoLabel}>구독권</span>
                                <span>{userInfo?.productName}</span>
                            </div>
                            <div css={userInfoRow}>
                                <span css={userInfoLabel}>이름</span>
                                <span>{userInfo?.uname}</span>
                            </div>
                        </div>
                        <button css={logoutBtnStyle} onClick={handleLogout}>
                            로그아웃
                        </button>
                    </>
                ) : (
                    <div css={loginFormStyle}>
                        <input
                            css={loginInputStyle}
                            type="text"
                            placeholder="로그인 정보 입력"
                            value={loginInput}
                            onChange={(e) => setLoginInput(e.currentTarget.value)}
                            onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) => {
                                if (evt.key === "Enter") {
                                    handleMatchSuccessWithEmail();
                                }
                            }}
                        />
                        <button css={loginBtnStyle} onClick={handleMatchSuccessWithEmail}>
                            로그인
                        </button>
                    </div>
                )}
            </div>

            {/* Route Navigation */}
            {isLogined && (
                <div css={navSectionStyle}>
                    {/* Search & Controls */}
                    <div css={controlBarStyle}>
                        <input
                            css={searchInputStyle}
                            type="text"
                            placeholder="페이지 검색 (이름 또는 경로)"
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.currentTarget.value)}
                        />
                        {!isSearching && (
                            <button css={toggleAllBtnStyle} onClick={toggleAll}>
                                {openGroups.size === ROUTE_GROUPS.length ? "모두 접기" : "모두 펼치기"}
                            </button>
                        )}
                    </div>

                    {/* Route Groups */}
                    {filteredGroups.map((group) => {
                        const originalIndex = ROUTE_GROUPS.findIndex((g) => g.title === group.title);
                        const isOpen = isSearching || openGroups.has(originalIndex);

                        return (
                            <div key={group.title} css={groupCardStyle}>
                                <button css={groupHeaderStyle} onClick={() => !isSearching && toggleGroup(originalIndex)}>
                                    <span css={groupTitleStyle}>
                                        {group.title}
                                        <span css={routeCountStyle}>{group.routes.length}</span>
                                    </span>
                                    {!isSearching && <span css={chevronStyle(isOpen)}>&#9662;</span>}
                                </button>
                                {isOpen && <div css={groupBodyStyle}>{group.routes.map(renderRouteButton)}</div>}
                            </div>
                        );
                    })}

                    {isSearching && filteredGroups.length === 0 && <div css={emptyStyle}>검색 결과가 없습니다.</div>}
                </div>
            )}
        </div>
    );
}

/* ─── Styles ─── */

const pageStyle = css`
    position: absolute;
    inset: 0;
    overflow-y: auto;
    background: #f5f5f7;
    padding: 20px 16px 40px;
    -webkit-overflow-scrolling: touch;
`;

const loginCardStyle = css`
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
`;

const loginFormStyle = css`
    display: flex;
    gap: 8px;
`;

const loginInputStyle = css`
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    &:focus {
        border-color: #ee5b7a;
    }
`;

const loginBtnStyle = css`
    padding: 10px 20px;
    background: #ee5b7a;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    &:active {
        opacity: 0.85;
    }
`;

const userInfoStyle = css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
`;

const userInfoRow = css`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
`;

const userInfoLabel = css`
    display: inline-block;
    min-width: 48px;
    padding: 2px 8px;
    background: #f0f0f2;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    color: #666;
    text-align: center;
`;

const logoutBtnStyle = css`
    width: 100%;
    padding: 10px;
    background: #f0f0f2;
    color: #555;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    &:active {
        background: #e4e4e6;
    }
`;

const navSectionStyle = css`
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const controlBarStyle = css`
    display: flex;
    gap: 8px;
    position: sticky;
    top: 0;
    z-index: 10;
    background: #f5f5f7;
    padding: 4px 0 8px;
`;

const searchInputStyle = css`
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 13px;
    background: #fff;
    outline: none;
    &:focus {
        border-color: #ee5b7a;
    }
`;

const toggleAllBtnStyle = css`
    padding: 8px 14px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 12px;
    color: #555;
    cursor: pointer;
    white-space: nowrap;
    &:active {
        background: #f0f0f2;
    }
`;

const groupCardStyle = css`
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

const groupHeaderStyle = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 14px 16px;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    &:active {
        background: #fafafa;
    }
`;

const groupTitleStyle = css`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #222;
`;

const routeCountStyle = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    background: #ee5b7a;
    color: #fff;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
`;

const chevronStyle = (isOpen: boolean) => css`
    font-size: 12px;
    color: #999;
    transition: transform 0.2s;
    transform: rotate(${isOpen ? "0deg" : "-90deg"});
`;

const groupBodyStyle = css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 12px 14px;
    border-top: 1px solid #f0f0f2;
    padding-top: 12px;
`;

const navBtnStyle = css`
    padding: 8px 14px;
    background: #f7f7f9;
    border: 1px solid #eee;
    border-radius: 8px;
    font-size: 12px;
    color: #333;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
    &:hover {
        background: #ee5b7a;
        color: #fff;
        border-color: #ee5b7a;
    }
    &:active {
        opacity: 0.85;
    }
`;

const routeCardStyle = css`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const routeLabelStyle = css`
    font-size: 11px;
    color: #888;
    padding-left: 2px;
`;

const multiButtonStyle = css`
    display: flex;
    gap: 6px;
`;

const emptyStyle = css`
    text-align: center;
    padding: 40px 0;
    color: #999;
    font-size: 13px;
`;

export default Login;
